import { useLoaderData, useRevalidator } from "react-router-dom";
import { NewRequestButton } from "../components/NewRequestButton";
import { RequestsTable } from "../components/RequestsTable";
import { Tabs, tabIds } from "../components/Tabs";
import { createRequest } from "../data/api";
import { useSearchFilter } from "../data/useSearchFilter";
import type { RequestsData } from "../data/loaders";

type RequestView = "status" | "owner";

const VIEWS: { key: RequestView; label: string }[] = [
  { key: "status", label: "By status" },
  { key: "owner", label: "By tech owner" },
];

export function Requests() {
  const { requests, accounts } = useLoaderData() as RequestsData;
  const revalidator = useRevalidator();
  // The view lives in the URL beside the table's filters, so a link, a reload
  // and the back button all land on the same one. By status is the default and
  // writes an empty value, so its canonical URL carries no `view` at all.
  const [params, setFilter] = useSearchFilter();
  const view: RequestView = params.get("view") === "owner" ? "owner" : "status";
  const showView = (next: RequestView) => setFilter("view", next === "status" ? "" : next);
  const ids = tabIds("req", view);

  return (
    <div className="mx-auto max-w-[1440px] px-6 py-8">
      <header className="mb-5 flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-display-xs font-semibold text-gray-900">Requests</h1>
        <NewRequestButton
          accounts={accounts}
          onCreated={async (payload) => {
            await createRequest(payload);
            revalidator.revalidate();
          }}
        />
      </header>

      <Tabs label="Requests view" prefix="req" tabs={VIEWS} value={view} onChange={showView} />

      {/* One panel, not both-mounted-and-hidden as the account page does: the
          two differ only in grouping, so mounting both would render every
          filter control and every Order box twice. Exactly one panel, labelled
          by the selected tab, is valid ARIA. */}
      <div role="tabpanel" id={ids.panel} aria-labelledby={ids.tab}>
        <RequestsTable requests={requests} accounts={accounts} groupBy={view} />
      </div>
    </div>
  );
}
